/**
 * @module app.um
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AuthService } from 'app/um/services/auth-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { localSort } from 'app/shared/lib/sorting';
import * as UM from 'app/um/models/essette-api';
import { EssTableCustomElement } from 'app/shared/elements/ess-table';

export interface CustomAuthDeterminationHistoryDetail extends UM.AuthDeterminationHistoryDetail {
    statusDescription?: string | null;
}

@autoinject()
export class DeterminationHistoryDetailGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.string()
    public determinationHistoryId?: string;

    @bindable.string()
    public determinationTimestamp: string = '';

    @bindable.string()
    public determinationAuthStatus: string = '';

    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    protected cacheDeterminationDetails: Record<string, { details: Array<object>, sorting?: Sorting }> = {};

    protected details: ReadonlyArray<CustomAuthDeterminationHistoryDetail> = [];

    private apiSorting: Sorting = new Sorting();
    private localSorting: Sorting = new Sorting();
    private localSortProperties: string[] = ['statusDescription'];

    private $detailsTable!: EssTableCustomElement<CustomAuthDeterminationHistoryDetail>;

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly authService: AuthService,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        let detailsFound = false;
        const detailObject = !!this.determinationHistoryId ? this.cacheDeterminationDetails[this.determinationHistoryId] : undefined;
        if(!!detailObject) {
            this.details = detailObject.details;
            detailsFound = true;
            if(!!detailObject.sorting) {
                this.apiSorting = detailObject.sorting;
            }
        }
        if(!detailsFound) {
            this.reload();
        }
    }

    protected attached(): void {
        if(this.apiSorting.entries.length > 0) {
            this.$detailsTable.applySorting(this.apiSorting);
        }
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;

        //If LocalSort
        if(this.localSortProperties.indexOf(property) > -1) {
            const { localSorting } = this;
            const details: CustomAuthDeterminationHistoryDetail[] = <CustomAuthDeterminationHistoryDetail[]>this.details;
            if(direction === null) {
                this.localSorting.remove(property);
                if(!this.localSorting.entries.length) this.reload();
            } else {
                this.localSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            localSort(details, localSorting);
        } else {
            const { apiSorting } = this;
            if(direction === null) {
                apiSorting.remove(property);
            } else {
                const sortDirection = direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending;

                if(apiSorting.entries.findIndex(entry => entry.reference === property && entry.direction === sortDirection) > -1) {
                    return;
                }
                else {
                    apiSorting.add({
                        reference: property,
                        direction: sortDirection
                    });
                }
            }
            this.reload();
        }
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        this.loadDeterminationDetails();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadDeterminationDetails(): Promise<void> {
        if(!this.determinationHistoryId) return;

        const options: Partial<IQueryLoadOptions> = {
            all: true,
            sorting: this.apiSorting
        };

        const includes = [
            'authServiceStatus',
            'medicationStatus'
        ];

        const fields = [
            'authServiceStatus.description',
            'code',
            'description',
            'determinationApplied',
            'medicationStatus.description',
            'quantity.approved',
            'quantity.requested',
            'triggeredBy',
            'type.code'
        ];

        try {
            this.state.beginLoad();
            const response = await this.authService.loadDeterminationDetails(this.determinationHistoryId, options, includes, fields);
            this.details = response.results;
            this.cacheDeterminationDetails[this.determinationHistoryId] = {
                details: <object[]>this.details,
                sorting: this.apiSorting
            };
            // Give each detail (both drugs and services) a shared statusDescription value to enable local sorting
            this.details.forEach(detail => detail.statusDescription = detail.authServiceStatus?.description || detail.medicationStatus?.description || '');
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
